import type { TerminalConfiguration } from "./types";
import { startupError, startupLog } from "./startupDiagnostics";

const DEVICE_TOKEN_KEY = "flextech.terminal.deviceToken";
const INSTALLATION_UUID_KEY = "flextech.terminal.installationUuid";
const OFFLINE_EVENTS_KEY = "flextech.terminal.offlineEvents";
const CONFIGURATION_CACHE_KEY = "flextech.player.configuration";
const PROCESSED_COMMAND_IDS_KEY = "flextech.player.processedCommandIds";
const terminalWindow = window as typeof window & {
  __FLEXTECH_TERMINAL_DEVICE_TOKEN__?: string;
  __FLEXTECH_TERMINAL_PREVIEW__?: boolean;
};
let previewMode = false;

type OfflineEvent = {
  client_event_id: string;
  event_type: string;
  payload: Record<string, unknown>;
};

const memoryStorage = new Map<string, string>();

function safeGetItem(key: string) {
  if (previewMode) return memoryStorage.get(key) || null;

  try {
    return window.localStorage.getItem(key) || memoryStorage.get(key) || null;
  } catch (error) {
    startupError("STORAGE_READ", error, { key });
    return memoryStorage.get(key) || null;
  }
}

function safeSetItem(key: string, value: string) {
  memoryStorage.set(key, value);
  if (previewMode) return;

  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    startupError("STORAGE_WRITE", error, { key });
  }
}

function safeRemoveItem(key: string) {
  memoryStorage.delete(key);
  if (previewMode) return;

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    startupError("STORAGE_REMOVE", error, { key });
  }
}

function handoffParams() {
  const hash = window.location.hash ? window.location.hash.replace(/^#/, "") : "";
  const source = hash || window.location.search.replace(/^\?/, "");
  return new URLSearchParams(source);
}

function applyRuntimeHandoff() {
  startupLog("STORAGE_INIT");

  try {
    const params = handoffParams();
    previewMode = terminalWindow.__FLEXTECH_TERMINAL_PREVIEW__ === true || params.get("preview") === "1";
    const token = terminalWindow.__FLEXTECH_TERMINAL_DEVICE_TOKEN__ || params.get("device_token") || params.get("deviceToken");
    const installationUuid = params.get("installation_uuid") || params.get("installationUuid");

    if (token) safeSetItem(DEVICE_TOKEN_KEY, token);
    if (installationUuid) safeSetItem(INSTALLATION_UUID_KEY, installationUuid);

    if (token && window.location.hash && window.history.replaceState) {
      window.history.replaceState(null, document.title, `${window.location.pathname}${window.location.search}`);
    }
  } catch (error) {
    startupError("STORAGE_INIT", error);
  }
}

applyRuntimeHandoff();

export const terminalStorage = {
  getDeviceToken() {
    return safeGetItem(DEVICE_TOKEN_KEY);
  },

  setDeviceToken(token: string) {
    safeSetItem(DEVICE_TOKEN_KEY, token);
  },

  clearDeviceToken() {
    safeRemoveItem(DEVICE_TOKEN_KEY);
  },

  getInstallationUuid() {
    let uuid = safeGetItem(INSTALLATION_UUID_KEY);
    if (!uuid) {
      uuid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      safeSetItem(INSTALLATION_UUID_KEY, uuid);
    }
    return uuid;
  },

  addOfflineEvent(event: Omit<OfflineEvent, "client_event_id"> & { client_event_id?: string }) {
    const events = this.getOfflineEvents();
    events.push({
      client_event_id: event.client_event_id || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`),
      event_type: event.event_type,
      payload: event.payload
    });
    safeSetItem(OFFLINE_EVENTS_KEY, JSON.stringify(events));
  },

  getOfflineEvents(): OfflineEvent[] {
    const raw = safeGetItem(OFFLINE_EVENTS_KEY);
    if (!raw) return [];

    try {
      return JSON.parse(raw) as OfflineEvent[];
    } catch (error) {
      startupError("STORAGE_PARSE", error, { key: OFFLINE_EVENTS_KEY });
      return [];
    }
  },

  removeOfflineEvent(clientEventId: string) {
    const events = this.getOfflineEvents().filter((event) => event.client_event_id !== clientEventId);
    safeSetItem(OFFLINE_EVENTS_KEY, JSON.stringify(events));
  },

  getCachedConfiguration(): TerminalConfiguration | null {
    const raw = safeGetItem(CONFIGURATION_CACHE_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as TerminalConfiguration;
    } catch (error) {
      startupError("STORAGE_PARSE", error, { key: CONFIGURATION_CACHE_KEY });
      return null;
    }
  },

  setCachedConfiguration(configuration: TerminalConfiguration) {
    safeSetItem(CONFIGURATION_CACHE_KEY, JSON.stringify(configuration));
  },

  clearCachedConfiguration() {
    safeRemoveItem(CONFIGURATION_CACHE_KEY);
  },

  getProcessedCommandIds(): string[] {
    const raw = safeGetItem(PROCESSED_COMMAND_IDS_KEY);
    if (!raw) return [];

    try {
      return JSON.parse(raw) as string[];
    } catch (error) {
      startupError("STORAGE_PARSE", error, { key: PROCESSED_COMMAND_IDS_KEY });
      return [];
    }
  },

  markCommandProcessed(commandId: string) {
    const ids = [commandId, ...this.getProcessedCommandIds().filter((id) => id !== commandId)].slice(0, 100);
    safeSetItem(PROCESSED_COMMAND_IDS_KEY, JSON.stringify(ids));
  }
};
